// src/Product/Vendors.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import {
  Search,
  PlusCircle,
  Trash2,
  CalendarDays,
  RefreshCcw,
  Eye,
  Pencil,
  X,
  Save,
  Users,
  Wallet,
  ReceiptText,
  RotateCcw,
  AlertCircle,
} from "lucide-react";
import "./Vendors.css";
import "./add-customer-modal.css";
import VendorMiniReportModal from "../Product/VendorMiniReportModal";
import AddVendorModal from "./AddVendorModal";

function money(n) {
  const v = Number(n || 0);
  return `₦${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function safeLower(x) {
  return String(x || "")
    .trim()
    .toLowerCase();
}

function EditVendorModal({ open, onClose, onSaved, companyId, vendor }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: "",
    company_name: "",
    address: "",
    phone: "",
  });

  useEffect(() => {
    if (!open) return;

    setErr("");
    setBusy(false);

    setForm({
      name: vendor?.vendor_name || "",
      company_name:
        vendor?.company_name && vendor.company_name !== "—"
          ? vendor.company_name
          : "",
      address: vendor?.address || "",
      phone: vendor?.phone_number || "",
    });
  }, [open, vendor]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function save() {
    setErr("");

    if (!companyId) return setErr("Missing company.");
    if (!vendor?.vendor_id) return setErr("Missing vendor id.");
    if (!String(form.name || "").trim()) {
      return setErr("Vendor name is required.");
    }

    setBusy(true);

    try {
      const payload = {
        name: String(form.name || "").trim(),
        company_name: String(form.company_name || "").trim() || null,
        address: String(form.address || "").trim() || null,
        phone: String(form.phone || "").trim() || null,
      };

      const { error } = await supabase
        .from("vendors")
        .update(payload)
        .eq("company_id", companyId)
        .eq("id", vendor.vendor_id);

      if (error) throw error;

      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Failed to update vendor.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="kp-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="kp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kp-modal-head">
          <div>
            <p className="kp-eyebrow">Vendor Details</p>
            <h2>Edit Vendor</h2>
            <p>Update the vendor’s basic profile information.</p>
          </div>

          <button className="kp-icon-btn" onClick={onClose} disabled={busy}>
            <X size={18} />
          </button>
        </div>

        <div className="kp-modal-form">
          <label>
            Vendor Name
            <input
              className="kp-input"
              placeholder="Vendor name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={busy}
            />
          </label>

          <label>
            Company Name
            <input
              className="kp-input"
              placeholder="Company name optional"
              value={form.company_name}
              onChange={(e) => setField("company_name", e.target.value)}
              disabled={busy}
            />
          </label>

          <label>
            Address
            <input
              className="kp-input"
              placeholder="Address optional"
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              disabled={busy}
            />
          </label>

          <label>
            Phone Number
            <input
              className="kp-input"
              placeholder="Phone number optional"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              disabled={busy}
            />
          </label>

          {err && <div className="kp-banner error">{err}</div>}
        </div>

        <div className="kp-modal-actions">
          <button className="kp-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="kp-btn primary" onClick={save} disabled={busy}>
            <Save size={16} />
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, hint, tone = "default" }) {
  return (
    <div className={`vendor-summary-card ${tone}`}>
      <div className="vendor-summary-icon">
        <Icon size={19} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}

export default function Vendors() {
  const { companyId } = useCompany();

  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const [miniOpen, setMiniOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editVendor, setEditVendor] = useState(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchVendorSummary = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setErrorMessage("");

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setErrorMessage("Not authenticated.");
        setBusy(false);
        return;
      }

      let baseList = [];

      const rpcRes = await supabase.rpc("get_vendor_trade_summary", {
        p_company_id: companyId,
        p_from: fromDate || null,
        p_to: toDate || null,
      });

      if (rpcRes?.error) {
        console.warn("get_vendor_trade_summary error:", rpcRes.error);
      } else {
        baseList = rpcRes?.data || [];
      }

      const vendorIdsFromRpc = (baseList || [])
        .map((r) => r.vendor_id)
        .filter(Boolean);

      let vendQ = supabase
        .from("vendors")
        .select("id,name,company_name,address,phone")
        .eq("company_id", companyId)
        .order("name", { ascending: true });

      if (vendorIdsFromRpc.length) {
        vendQ = vendQ.in("id", vendorIdsFromRpc);
      }

      const { data: vendors, error: vendErr } = await vendQ;
      if (vendErr) throw vendErr;

      const vendorList = vendors || [];
      const vendorIds = vendorList.map((vendor) => vendor.id);

      if (!vendorIds.length) {
        setRows([]);
        setFiltered([]);
        return;
      }

      let purQ = supabase
        .from("purchase_transactions")
        .select("id,vendor_id,total,payment_status,purchase_date")
        .eq("company_id", companyId)
        .in("vendor_id", vendorIds);

      if (fromDate) purQ = purQ.gte("purchase_date", fromDate);
      if (toDate) purQ = purQ.lte("purchase_date", toDate);

      const { data: purchases, error: purErr } = await purQ;
      if (purErr) throw purErr;

      const purList = purchases || [];
      const purchaseIds = purList.map((purchase) => purchase.id);

      let setsByPurchase = {};
      if (purchaseIds.length) {
        const { data: sets, error: setErr } = await supabase
          .from("purchase_settlements")
          .select("purchase_id,type,amount")
          .eq("company_id", companyId)
          .in("purchase_id", purchaseIds);

        if (setErr) throw setErr;

        (sets || []).forEach((settlement) => {
          (setsByPurchase[settlement.purchase_id] ||= []).push(settlement);
        });
      }

      const aggByVendor = new Map();

      vendorIds.forEach((vendorId) => {
        aggByVendor.set(vendorId, {
          billed_total: 0,
          paid_total: 0,
          refunded_total: 0,
          balance_due: 0,
        });
      });

      purList.forEach((purchase) => {
        const vendorId = purchase.vendor_id;
        if (!aggByVendor.has(vendorId)) return;

        const total = Number(purchase.total || 0);
        const status = String(
          purchase.payment_status || "UNPAID"
        ).toUpperCase();
        const settlements = setsByPurchase[purchase.id] || [];

        const paidFromSettlements = settlements
          .filter((item) => String(item.type || "").toUpperCase() === "PAYMENT")
          .reduce((sum, item) => sum + Number(item.amount || 0), 0);

        const refunded = settlements
          .filter((item) => String(item.type || "").toUpperCase() === "REFUND")
          .reduce((sum, item) => sum + Number(item.amount || 0), 0);

        const basePaid = status === "PAID" ? total : 0;
        const paid = basePaid + paidFromSettlements;
        const balance = total - (paid - refunded);

        const agg = aggByVendor.get(vendorId);
        agg.billed_total += total;
        agg.paid_total += paid;
        agg.refunded_total += refunded;
        agg.balance_due += balance;
      });

      const rpcById = new Map(
        (baseList || []).map((row) => [row.vendor_id, row])
      );

      const finalRows = vendorList.map((vendor) => {
        const rpc = rpcById.get(vendor.id) || {};
        const agg = aggByVendor.get(vendor.id) || {
          billed_total: 0,
          paid_total: 0,
          refunded_total: 0,
          balance_due: 0,
        };

        return {
          vendor_id: vendor.id,
          vendor_name: rpc.vendor_name || vendor.name || "—",
          company_name: rpc.company_name || vendor.company_name || "—",
          address: vendor.address || null,
          phone_number: vendor.phone || null,
          billed_total: agg.billed_total,
          paid_total: agg.paid_total,
          refunded_total: agg.refunded_total,
          balance_due: agg.balance_due,
        };
      });

      setRows(finalRows);
      setFiltered(finalRows);
    } catch (e) {
      console.error(e);
      setErrorMessage(e?.message || "Failed to load vendor summary.");
      setRows([]);
      setFiltered([]);
    } finally {
      setBusy(false);
    }
  }, [companyId, fromDate, toDate]);

  useEffect(() => {
    fetchVendorSummary();
  }, [fetchVendorSummary]);

  useEffect(() => {
    const q = safeLower(searchQuery);

    if (!q) {
      setFiltered(rows);
      return;
    }

    setFiltered(
      (rows || []).filter((row) => {
        const name = safeLower(row.vendor_name);
        const company = safeLower(row.company_name);
        const address = safeLower(row.address);
        const phone = safeLower(row.phone_number);

        return (
          name.includes(q) ||
          company.includes(q) ||
          address.includes(q) ||
          phone.includes(q)
        );
      })
    );
  }, [searchQuery, rows]);

  const totals = useMemo(() => {
    const list = filtered || [];

    const billed = list.reduce(
      (sum, row) => sum + Number(row.billed_total || 0),
      0
    );
    const paid = list.reduce(
      (sum, row) => sum + Number(row.paid_total || 0),
      0
    );
    const refunded = list.reduce(
      (sum, row) => sum + Number(row.refunded_total || 0),
      0
    );
    const balance = list.reduce(
      (sum, row) => sum + Number(row.balance_due || 0),
      0
    );

    return {
      count: list.length,
      billed,
      paid,
      refunded,
      balance,
      paymentRate: billed > 0 ? (paid / billed) * 100 : 0,
    };
  }, [filtered]);

  const handleDelete = async (vendorId) => {
    if (!window.confirm("Delete this vendor?")) return;

    const { error } = await supabase
      .from("vendors")
      .delete()
      .eq("id", vendorId)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error deleting vendor:", error);
      alert("Could not delete vendor.");
      return;
    }

    alert("Vendor deleted.");
    fetchVendorSummary();
  };

  function clearFilters() {
    setSearchQuery("");
    setFromDate("");
    setToDate("");
  }

  return (
    <div className="vendors-page">
      <div className="vendor-hero">
        <div>
          <p className="kp-eyebrow">Vendors</p>
          <h1>Vendor Payables</h1>
          <p>
            Manage suppliers, review purchases, track payments, and monitor
            outstanding balances.
          </p>
        </div>

        <button
          className="kp-btn primary"
          onClick={() => setShowModal(true)}
          disabled={busy}
        >
          <PlusCircle size={17} />
          Add New Vendor
        </button>
      </div>

      {errorMessage && (
        <div className="kp-banner error">
          <AlertCircle size={16} />
          {errorMessage}
        </div>
      )}

      <section className="vendor-summary-grid">
        <SummaryCard
          icon={Users}
          label="Vendors"
          value={totals.count}
          hint="Based on current filters"
        />
        <SummaryCard
          icon={ReceiptText}
          label="Total Purchases"
          value={money(totals.billed)}
          hint="Supplier bills and purchases"
        />
        <SummaryCard
          icon={Wallet}
          label="Amount Paid"
          value={money(totals.paid)}
          hint={`${totals.paymentRate.toFixed(1)}% payment rate`}
          tone="success"
        />
        <SummaryCard
          icon={RotateCcw}
          label="Refunds"
          value={money(totals.refunded)}
          hint="Refunds received"
          tone="warning"
        />
        <SummaryCard
          icon={AlertCircle}
          label="Balance Due"
          value={money(totals.balance)}
          hint={totals.balance > 0 ? "Outstanding payables" : "No balance due"}
          tone={totals.balance > 0 ? "danger" : "success"}
        />
      </section>

      <section className="kp-panel vendor-filter-panel">
        <div className="vendor-filter-top">
          <div>
            <h3>Vendor List</h3>
            <p>
              Search, filter by period, view vendor reports, or update details.
            </p>
          </div>

          {busy && <span className="kp-loading-pill">Loading…</span>}
        </div>

        <div className="vendor-filter-grid">
          <div className="kp-search-wrap">
            <Search size={17} />
            <input
              type="text"
              placeholder="Search vendor, company, phone or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="vendor-period-wrap">
            <span className="period-label">
              <CalendarDays size={15} />
              Period
            </span>

            <input
              className="kp-input"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              disabled={busy}
              title="From date"
            />

            <input
              className="kp-input"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              disabled={busy}
              title="To date"
            />
          </div>

          <div className="vendor-filter-actions">
            <button
              className="kp-btn ghost"
              onClick={fetchVendorSummary}
              disabled={busy}
            >
              <RefreshCcw size={16} />
              Refresh
            </button>

            <button
              className="kp-btn light"
              onClick={clearFilters}
              disabled={busy}
            >
              Clear
            </button>
          </div>
        </div>
      </section>

      <section className="kp-panel vendor-table-panel">
        <div className="vendor-table-head">
          <div>
            <h3>Vendors</h3>
            <p>
              Showing <strong>{filtered.length}</strong> vendor(s)
            </p>
          </div>
        </div>

        <div className="vendor-table-wrap">
          <table className="vendor-table modern">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Contact</th>
                <th className="num">Purchases</th>
                <th className="num">Paid</th>
                <th className="num">Refunds</th>
                <th className="num">Balance Due</th>
                <th className="center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((row) => (
                <tr key={row.vendor_id}>
                  <td>
                    <div className="vendor-name-block">
                      <strong>{row.vendor_name || "—"}</strong>
                      <span>{row.company_name || "No company name"}</span>
                    </div>
                  </td>

                  <td>
                    <div className="vendor-contact-block">
                      <span>{row.phone_number || "No phone number"}</span>
                      <small>{row.address || "No address saved"}</small>
                    </div>
                  </td>

                  <td className="num">{money(row.billed_total)}</td>
                  <td className="num success-text">{money(row.paid_total)}</td>
                  <td className="num warning-text">
                    {money(row.refunded_total)}
                  </td>
                  <td
                    className={`num ${
                      Number(row.balance_due || 0) > 0 ? "danger-text" : ""
                    }`}
                  >
                    <strong>{money(row.balance_due)}</strong>
                  </td>

                  <td className="center">
                    <div className="vendor-action-row">
                      <button
                        className="kp-action-btn"
                        title="Mini report"
                        onClick={() => {
                          setSelectedVendor(row);
                          setMiniOpen(true);
                        }}
                        disabled={busy}
                      >
                        <Eye size={15} />
                        Report
                      </button>

                      <button
                        className="kp-action-btn"
                        title="Edit vendor"
                        onClick={() => {
                          setEditVendor(row);
                          setEditOpen(true);
                        }}
                        disabled={busy}
                      >
                        <Pencil size={15} />
                        Edit
                      </button>

                      <button
                        className="kp-icon-btn danger"
                        title="Delete vendor"
                        onClick={() => handleDelete(row.vendor_id)}
                        disabled={busy}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!filtered.length && !busy && (
            <div className="vendor-empty-state">
              <div className="vendor-empty-icon">
                <Users size={26} />
              </div>
              <h3>No vendors found</h3>
              <p>
                Try clearing your filters or add a new vendor to begin tracking
                payables.
              </p>
              <button
                className="kp-btn primary"
                onClick={() => setShowModal(true)}
              >
                <PlusCircle size={16} />
                Add Vendor
              </button>
            </div>
          )}
        </div>
      </section>

      {showModal && (
        <AddVendorModal
          onClose={() => setShowModal(false)}
          onAdd={() => {
            setShowModal(false);
            fetchVendorSummary();
          }}
        />
      )}

      <VendorMiniReportModal
        open={miniOpen}
        onClose={() => setMiniOpen(false)}
        companyId={companyId}
        vendor={selectedVendor}
        fromDate={fromDate || ""}
        toDate={toDate || ""}
      />

      <EditVendorModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => fetchVendorSummary()}
        companyId={companyId}
        vendor={editVendor}
      />
    </div>
  );
}
